// Actual domain logic should be in a domainLogic.js file, at the appropriate
// level in the hierarchy.
import { IN_TRANSACTION } from "appConfig";

import { Decimal, Money } from "classes/DecimalClasses";

import {
  changeCustomer,
  updateLineItemAvailable,
} from "services/sosInventoryService/salesTransaction/domainLogic";
import { getRecord } from "services/sosInventoryService/sosApi";
import { handleProgramError } from "services/utility/errors";
import { formatCardOnFile } from "services/utility/formatting";

import globalState from "globalState/globalState";

import { OBJECT_TYPES, SOS_PAY_CREDIT, SOS_PAY_ACH } from "appConstants";

const OBJECT_TYPE = OBJECT_TYPES.SALES_RECEIPT.fullString;

export async function updateTransaction(
  field,
  newValue,
  transactionCustomFieldDefs,
  customerCustomFieldDefs,
  currentState,
  taxCodes,
  updaters,
  currencies
) {
  const { record, lines, relatedRecords } = currentState;
  if (!record || !lines || !relatedRecords) {
    handleProgramError(new Error("update Transaction | invalid currentState"));
  }

  let newRecord, newLines, newRelatedRecords;

  switch (field) {
    case "date":
      newRecord = { ...record, date: newValue };
      updaters.setRecord(newRecord);
      if (newValue) {
        newLines = await updateLineItemAvailable(newRecord, lines, OBJECT_TYPE);
      }

      break;

    case "location":
      newRecord = { ...record, location: newValue };
      const { taxEnabled, connectedToQBO, defaultTaxRate } =
        globalState.getState().userCompanySettings.settings;
      if (
        taxEnabled &&
        !connectedToQBO &&
        !defaultTaxRate &&
        !isNaN(Number(newValue.salesTaxRate))
      ) {
        // fallback to location's sales tax rate if no default is set and not using QBO
        newRecord.taxPercent = new Decimal(newValue.salesTaxRate);
      }
      updaters.setRecord(newRecord);
      newLines = await updateLineItemAvailable(newRecord, lines, OBJECT_TYPE);

      break;

    case "customer":
      if (!newValue) {
        newRecord = {
          ...record,
          customer: null,
          accountToken: null,
          paymentInfo: null,
          hasCardOnFile: null,
        };
        updaters.setRecord(newRecord);
        newRelatedRecords = { ...relatedRecords, customer: null };
        break;
      }

      const recordData = await changeCustomer(newValue.id, {
        record,
        customerCustomFieldDefs,
        transactionCustomFieldDefs,
        transactionCustomFields: record.customFields,
        lines,
        taxCodes,
      });

      const priceTier = recordData.customer?.priceTier
        ? await getRecord(
            "pricetier",
            recordData.customer.priceTier.id,
            IN_TRANSACTION
          )
        : null;

      newRecord = { ...recordData.newRecord, priceTier };
      const { hasCardOnFile, expMonth, expYear, paymentMethod } =
        recordData.customer;

      if (paymentMethod) {
        newRecord.paymentMethod = paymentMethod;
      } else if (hasCardOnFile) {
        const isCard = expMonth > 0 && expYear > 0;
        newRecord.paymentMethod = isCard
          ? currentState.paymentMethods.find(
              ({ sosPayType }) => sosPayType === SOS_PAY_CREDIT
            )
          : currentState.paymentMethods.find(
              ({ sosPayType }) => sosPayType === SOS_PAY_ACH
            );
      }

      if (hasCardOnFile) {
        newRecord.paymentInfo = formatCardOnFile(null, recordData.customer);
        newRecord.hasCardOnFile = true;
        newRecord.storeCustomerToken = true;
      } else {
        newRecord.accountToken = null;
        newRecord.paymentInfo = null;
        newRecord.hasCardOnFile = false;
        newRecord.storeCustomerToken = false;
      }

      updaters.setRecord(newRecord);
      newLines = recordData.newLines;
      newRelatedRecords = { ...relatedRecords, customer: recordData.customer };

      break;

    case "accountToken":
      newRecord = {
        ...record,
        [field]: newValue.token,
        paymentInfo: newValue.paymentInfo,
        storeCustomerToken: false,
        hasCardOnFile: true,
        showKeepOnFile: true,
      };
      updaters.setRecord(newRecord);

      break;

    case "paymentMethod":
    case "assignedToUser":
    case "billing":
    case "channel":
    case "checkNumber":
    case "comment":
    case "customerMessage":
    case "customerNotes":
    case "customerPO":
    case "customFields":
    case "department":
    case "depositAccount":
    case "dropShip":
    case "number":
    case "orderStage":
    case "priceTier":
    case "priority":
    case "salesRep":
    case "shipDate":
    case "shipping":
    case "shippingMethod":
    case "statusMessage":
    case "storeCustomerToken":
    case "trackingNumber":
    case "transactionLocationQuickBooks":
      newRecord = { ...record, [field]: newValue };
      updaters.setRecord(newRecord);
      break;

    case "exchangeRate":
      newRecord = { ...record, [field]: new Decimal(newValue || 0) };
      updaters.setRecord(newRecord);
      break;

    case "shippingAmount":
      newRecord = {
        ...record,
        shippingAmount: new Money(newValue || 0),
        shippingUpdated: true,
      };
      updaters.setRecord(newRecord);
      break;

    case "currency":
      updaters.setRecord((prev) => ({
        ...prev,
        currency: newValue.id ? { id: newValue.id, name: newValue.name } : null,
        exchangeRate: newValue.id
          ? currencies.find(({ id }) => id === newValue.id).exchangeRate
          : null,
      }));
      break;

    default:
      handleProgramError(
        new Error(`updateTransaction | unknown field (${field})`)
      );
  }

  if (newLines) {
    updaters.lineHandler({ type: "set", lines: newLines });
  }
  if (newRelatedRecords) {
    updaters.setRelatedRecords(newRelatedRecords);
  }
}
